import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, PlusCircle, Loader2, Clock, CheckCircle2, Award } from "lucide-react";
import HomeworkDetail from "./HomeworkDetail";
import { formatReceivedAt } from "../../lib/dateFormat";

/**
 * V165 — Homework MVP entry point inside PortalPage.
 *
 * Party-scoped: the tutor sees a Post button + submission counts, the
 * parent only sees published homeworks with their submission status.
 * The heavy work (upload, camera, evaluate) lives inside HomeworkDetail.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API });

export default function HomeworkPanel({ party, token }) {
    const isTutor = party === "tutor";
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);
    const [creating, setCreating] = useState(false);
    const [showCreate, setShowCreate] = useState(false);

    const reload = useCallback(async () => {
        try {
            const r = await publicApi.get(`/portal/${party}/${token}/homeworks?_=${Date.now()}`);
            setItems(r.data?.items || []);
        } catch (_e) { /* transient */ }
        finally { setLoading(false); }
    }, [party, token]);

    useEffect(() => { reload(); }, [reload]);

    const createDraft = async (payload) => {
        setCreating(true);
        try {
            const r = await publicApi.post(`/portal/tutor/${token}/homeworks`, payload);
            // Auto-publish so parents can see it immediately — kept as a
            // separate API call so a future tutor "save as draft" UX can
            // simply skip the publish step.
            await publicApi.post(`/portal/tutor/${token}/homeworks/${r.data.id}/publish`);
            toast.success("Homework posted");
            setShowCreate(false);
            setOpenId(r.data.id);
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not post homework");
        } finally {
            setCreating(false);
        }
    };

    return (
        <section data-testid={`portal-homework-${party}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2 flex items-center gap-1.5">
                <BookOpen className="w-3 h-3" /> Homework
                {items.length > 0 && <span className="text-slate-400 font-normal">· {items.length}</span>}
                {isTutor && (
                    <button
                        type="button"
                        onClick={() => setShowCreate(true)}
                        data-testid="homework-post-btn"
                        data-cm-primary
                        className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-cyan-600 text-white px-3 py-1.5 rounded-full hover:bg-cyan-700"
                    >
                        <PlusCircle className="w-3.5 h-3.5" /> Post homework
                    </button>
                )}
            </div>

            {loading ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center text-slate-400 text-xs">
                    <Loader2 className="w-4 h-4 mx-auto animate-spin" />
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-6 text-center text-xs text-slate-500">
                    {isTutor
                        ? "No homework yet. Tap “Post homework” to send the first one."
                        : "No homework yet. Your tutor will post assignments here."}
                </div>
            ) : (
                <ul className="space-y-2" data-testid="homework-list">
                    {items.map((hw) => (
                        <HomeworkListRow
                            key={hw.id}
                            hw={hw}
                            isTutor={isTutor}
                            onOpen={() => setOpenId(hw.id)}
                        />
                    ))}
                </ul>
            )}

            {openId && (
                <HomeworkDetail
                    party={party}
                    token={token}
                    homeworkId={openId}
                    onClose={() => setOpenId(null)}
                    onChanged={reload}
                />
            )}

            {showCreate && (
                <CreateHomeworkDialog
                    creating={creating}
                    onCancel={() => setShowCreate(false)}
                    onSubmit={createDraft}
                />
            )}
        </section>
    );
}

// ---------------------------------------------------------- List row

function HomeworkListRow({ hw, isTutor, onOpen }) {
    const sub = hw.submission;
    const deadline = hw.deadline;
    const overdue = deadline && new Date(deadline) < new Date() && !sub;
    let statusLabel = "Pending";
    let statusTone = "bg-slate-100 text-slate-700";
    let StatusIcon = Clock;
    if (sub?.status === "evaluated") {
        statusLabel = `Marked · ${sub.marks ?? "—"}${sub.marks_out_of ? `/${sub.marks_out_of}` : ""}`;
        statusTone = "bg-emerald-50 text-emerald-800 border-emerald-200";
        StatusIcon = Award;
    } else if (sub?.status === "submitted") {
        statusLabel = "Submitted";
        statusTone = "bg-cyan-50 text-cyan-800 border-cyan-200";
        StatusIcon = CheckCircle2;
    } else if (overdue) {
        statusLabel = "Overdue";
        statusTone = "bg-rose-50 text-rose-700 border-rose-200";
    }

    return (
        <li>
            <button
                type="button"
                onClick={onOpen}
                data-testid={`homework-row-${hw.id}`}
                className="w-full text-left bg-white border border-slate-200 rounded-2xl p-3.5 hover:border-cyan-300 active:scale-[0.99] transition-all"
            >
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <div className="text-sm font-heading font-bold text-[#0F172A] truncate">{hw.title}</div>
                        {hw.subject && <div className="text-[11px] text-slate-500 mt-0.5">{hw.subject}</div>}
                    </div>
                    <span
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusTone} shrink-0`}
                        data-testid={`homework-row-status-${hw.id}`}
                    >
                        <StatusIcon className="w-3 h-3" />
                        {statusLabel}
                    </span>
                </div>
                {hw.description && (
                    <p className="text-xs text-slate-600 mt-1 line-clamp-2">{hw.description}</p>
                )}
                <div className="flex items-center justify-between mt-2 text-[11px] text-slate-500">
                    <span>
                        {deadline ? <>Due {formatReceivedAt(deadline)}</> : "No deadline"}
                    </span>
                    {isTutor && sub?.status === "submitted" && (
                        <span className="text-cyan-700 font-semibold" data-testid={`homework-row-needs-grading-${hw.id}`}>Needs grading</span>
                    )}
                </div>
            </button>
        </li>
    );
}

// ---------------------------------------------------------- Create dialog

function CreateHomeworkDialog({ creating, onCancel, onSubmit }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [subject, setSubject] = useState("");
    const [deadline, setDeadline] = useState("");
    const canSubmit = title.trim().length >= 2 && !creating;

    const submit = (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onSubmit({
            title: title.trim(),
            description: description.trim() || null,
            subject: subject.trim() || null,
            deadline: deadline ? new Date(deadline).toISOString() : null,
        });
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 z-[95] flex items-start sm:items-center justify-center overflow-y-auto p-0 sm:p-4 classroom-mobile"
            data-testid="homework-create-dialog"
        >
            <form
                onSubmit={submit}
                className="bg-white w-full sm:max-w-md sm:rounded-2xl shadow-2xl min-h-screen sm:min-h-0 sm:max-h-[92vh] overflow-y-auto"
            >
                <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-5 py-3 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-cyan-600 font-bold">Post Homework</div>
                        <div className="text-lg font-heading font-bold text-[#0F172A]">Send to student</div>
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={creating}
                        data-testid="homework-create-close"
                        data-cm-compact
                        aria-label="Cancel"
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 disabled:opacity-50"
                    >
                        ✕
                    </button>
                </div>
                <div className="p-4 sm:p-5 space-y-4">
                    <Field label="Title *">
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            data-testid="homework-create-title"
                            placeholder="Trigonometric Identities — Ch 3"
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                        />
                    </Field>
                    <Field label="Instructions">
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            data-testid="homework-create-description"
                            rows={4}
                            placeholder="What should the student solve? Textbook Q1-5, worksheet, etc."
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none resize-none"
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Subject">
                            <input
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                data-testid="homework-create-subject"
                                placeholder="Math"
                                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                            />
                        </Field>
                        <Field label="Deadline">
                            <input
                                type="datetime-local"
                                value={deadline}
                                onChange={(e) => setDeadline(e.target.value)}
                                data-testid="homework-create-deadline"
                                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                            />
                        </Field>
                    </div>
                    <p className="text-[11px] text-slate-500">
                        You can add a question PDF or photo after posting. The homework is visible to the parent immediately.
                    </p>
                </div>
                <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 sm:px-5 py-3 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={creating}
                        className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={!canSubmit}
                        data-testid="homework-create-submit"
                        data-cm-primary
                        className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#1E293B] disabled:opacity-60"
                    >
                        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
                        Post homework
                    </button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">{label}</div>
            {children}
        </label>
    );
}
